import { memo } from 'react';
import { phaseTypeLabels } from 'src/apis/monolith/mappings';
import type { AuctionPhaseType } from 'src/apis/monolith/types';
import type { StatusProps } from 'src/components/data-display/status-display';
import { StatusDisplay } from 'src/components/data-display/status-display';

type AuctionStatusProps = {
  phase: AuctionPhaseType;
  suspended?: boolean;
  runningAuctions?: boolean;
  isActivityCard?: boolean;
  appendix?: StatusProps['appendix'];
  'data-testid'?: string;
};

export const auctionStatusVariantMap: Record<
  AuctionPhaseType,
  StatusProps['variant']
> = {
  PREOPEN: 'warning',
  PUBLISHED: 'warning',
  OPEN: 'ready',
  EVALUATING: 'warning',
  CLOSED: 'warning',
  ECONOMIC_TEST_PENDING: 'warning',
  FINISHED: 'success',
  SKIPPED: 'error',
  CANCELLED: 'error',
  TERMINATED: 'error',
  UNSUCCESSFUL: 'error',
};

const activityPhaseTypeLabels: Record<AuctionPhaseType, string> = {
  FINISHED: 'Auction is Finished',
  CANCELLED: 'Auction is Cancelled',
  UNSUCCESSFUL: 'Unsuccessful',
  TERMINATED: 'Auction is Terminated',
  PREOPEN: 'Preopen',
  PUBLISHED: 'Auction is Published',
  OPEN: 'Auction Round is Open',
  EVALUATING: 'Evaluating',
  CLOSED: 'Auction Round is Closed',
  SKIPPED: 'Skipped',
  ECONOMIC_TEST_PENDING: 'Economic Test Pending',
};

function getAuctionStatus({
  phase,
  suspended,
  runningAuctions,
  isActivityCard,
}: {
  phase: AuctionPhaseType;
  suspended?: boolean;
  runningAuctions?: boolean;
  isActivityCard?: boolean;
}): Pick<StatusProps, 'status' | 'variant'> {
  const labels = isActivityCard ? activityPhaseTypeLabels : phaseTypeLabels;

  let status = labels[phase];
  let variant = auctionStatusVariantMap[phase];

  if (phase === 'OPEN' && suspended) {
    status = isActivityCard ? 'Auction is Suspended' : 'Suspended';
    variant = 'warning';
  }

  if (phase === 'CLOSED' && runningAuctions) {
    status = 'Waiting for Next Round';
  }

  return { status, variant };
}

export const AuctionStatusDisplay = memo<AuctionStatusProps>(
  ({
    phase,
    runningAuctions,
    suspended,
    isActivityCard,
    appendix,
    ['data-testid']: testId = 'auction-status',
  }) => {
    return (
      <StatusDisplay
        {...getAuctionStatus({
          phase,
          runningAuctions,
          suspended,
          isActivityCard,
        })}
        appendix={appendix}
        data-testid={testId}
      />
    );
  }
);
